import { BRIEF_PIPELINE_STEPS, type BriefStepKey } from './pipeline';

/**
 * A stopped Brief run, in words a person can act on.
 *
 * `brief_runs.error_message` is the TECHNICAL record and stays that way: it is
 * what the durable runtime threw, wrapped as `FatalError: Step
 * "step//./src/lib/workflows/brief-order-workflow//composePiecesStep" failed
 * after 3 retries: Could not persist Brief Concept at source ordinal 0: new row
 * for relation "project_tasks" violates check constraint ...` (ITC run
 * 621dcfdb, 2026-10-01, shown verbatim in the New brief dialog). Every surface
 * that shows a stopped run goes through `formatBriefRunFailure`; none prints
 * the stored string.
 */

export interface BriefRunFailure {
  stepKey: BriefStepKey | null;
  /** The step's human name ("Compose the pieces"), or null when it cannot be told. */
  stepLabel: string | null;
  kind: 'persist' | 'model' | 'source' | 'gap' | 'unknown';
  /** What happened and what to do, with no step name. Safe as a step's finding. */
  sentence: string;
  /** Step name + sentence: the one string a dialog or card prints. */
  text: string;
}

export interface BriefRunFailureStepRef {
  stepKey?: string;
  label?: string;
  status: string;
}

const STEP_FUNCTIONS: Record<string, BriefStepKey> = {
  anchorSource: 'anchor_source',
  classifyOfferStep: 'classify_offer',
  loadBrand: 'load_brand',
  deriveAudiencesStep: 'derive_audiences',
  pullWinners: 'pull_winners',
  decodeWinnersStep: 'decode_winners',
  mapCompetitors: 'map_competitors',
  researchAngles: 'research_angles',
  pickReferences: 'pick_references',
  composePiecesStep: 'compose_pieces',
  writePaidCopyStep: 'write_paid_copy',
};

/** What the person lost when the write was refused, per step. */
const PERSIST_OBJECT: Partial<Record<BriefStepKey, string>> = {
  anchor_source: 'the source it read',
  derive_audiences: 'the audiences it found',
  research_angles: 'the angles it researched',
  compose_pieces: 'the Concepts it wrote',
  write_paid_copy: 'the ad copy it wrote',
};

const MARKER = /Database rejected the write/i;
const DB_REFUSAL =
  /violates (?:check|not-null|foreign key) constraint|invalid input syntax for type|null value in column|permission denied for|Could not persist/i;
const MODEL =
  /resource exhausted|\b429\b|rate[ _-]?limit|quota|timeout|timed out|no output generated|no object generated|did not match schema|overloaded|unavailable/i;
const SOURCE =
  /readability check|no resolved source|catalog product not found|could not (?:read|fetch) the source/i;
const GAP = /steps never run/i;

const LABEL_BY_KEY = new Map<string, string>(BRIEF_PIPELINE_STEPS.map((s) => [s.key, s.label]));

function stepFromMessage(raw: string): BriefStepKey | null {
  const match = raw.match(/\/\/([A-Za-z]+)"/);
  return (match && STEP_FUNCTIONS[match[1]]) || null;
}

function keyOfStep(step: BriefRunFailureStepRef): BriefStepKey | null {
  if (step.stepKey && LABEL_BY_KEY.has(step.stepKey)) return step.stepKey as BriefStepKey;
  const byLabel = BRIEF_PIPELINE_STEPS.find((s) => s.label === step.label);
  return byLabel?.key ?? null;
}

function stepFromSteps(steps: readonly BriefRunFailureStepRef[] | undefined): BriefStepKey | null {
  const hit =
    steps?.find((s) => s.status === 'failed' && keyOfStep(s)) ??
    steps?.find((s) => s.status === 'running' && keyOfStep(s));
  return hit ? keyOfStep(hit) : null;
}

export function formatBriefRunFailure(
  rawMessage: string | null | undefined,
  steps?: readonly BriefRunFailureStepRef[],
): BriefRunFailure {
  const raw = (rawMessage ?? '').trim();
  const stepKey = stepFromMessage(raw) ?? stepFromSteps(steps);
  const stepLabel = stepKey ? (LABEL_BY_KEY.get(stepKey) ?? null) : null;

  let kind: BriefRunFailure['kind'];
  let sentence: string;
  if (MARKER.test(raw) || DB_REFUSAL.test(raw)) {
    kind = 'persist';
    const object = (stepKey && PERSIST_OBJECT[stepKey]) ?? 'what it wrote';
    sentence = `Mora couldn't save ${object}. This is a Mora bug, not your source — try again, or contact support if it repeats.`;
  } else if (SOURCE.test(raw)) {
    kind = 'source';
    sentence =
      "Mora couldn't read enough from the source to build a Brief. Check that the link opens in a browser, or describe the offer as a topic instead.";
  } else if (MODEL.test(raw)) {
    kind = 'model';
    sentence =
      "The writing model didn't give Mora a usable answer. Nothing about your source caused this — try again in a few minutes.";
  } else if (GAP.test(raw)) {
    kind = 'gap';
    sentence =
      'The run ended before every step finished. Try again, or contact support if it repeats.';
  } else {
    kind = 'unknown';
    sentence = 'The run stopped unexpectedly. Try again, or contact support if it repeats.';
  }

  const text = stepLabel ? `${stepLabel} stopped. ${sentence}` : sentence;
  return { stepKey, stepLabel, kind, sentence, text };
}

/**
 * The 7–10 minute estimate and "it keeps running" are true only while a run is
 * queued or running. Left up over a stopped run they sit two lines above the
 * failure and contradict it (ITC run 621dcfdb), so a settled run replaces both.
 * Null while the run is live: the caller then keeps its in-flight copy.
 */
export function settledRunHeader(
  status: string | null | undefined,
): { title: string; description: string } | null {
  if (status === 'failed') {
    return {
      title: 'Brief stopped',
      description: 'Stopped before it finished. What it did get is below.',
    };
  }
  if (status === 'succeeded') {
    return { title: 'Brief ready', description: 'Finished. Nothing is still running.' };
  }
  if (status === 'cancelled') {
    return {
      title: 'Brief cancelled',
      description: 'Cancelled. What it got before stopping is below.',
    };
  }
  return null;
}
